import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { 
  getUserTags, 
  getSortingTemplates, 
  renameFile, 
  updateFileTags, 
  applySortingTemplate,
  createTag,
  addTagToFiles
} from '../../services/api';

// Keep visual language consistent with UploadReviewModal
const QuickEditModal = ({ file, onClose, onSaved }) => {
  const [nameInput, setNameInput] = useState(file?.filename || '');
  const [allTags, setAllTags] = useState([]); // [{ tag_id, name, color, icon }]
  const [selectedTags, setSelectedTags] = useState(file?.tags || []); // using names for now to match existing UI
  const [newTagDraft, setNewTagDraft] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [keyToValues, setKeyToValues] = useState({});
  const [valueSuggestions, setValueSuggestions] = useState([]);
  const [showValueSuggestions, setShowValueSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const tags = await getUserTags();
        setAllTags(Array.isArray(tags) ? tags : []);
      } catch (_) {
        setAllTags([]);
      }
      try {
        const tmpls = await getSortingTemplates();
        setTemplates(Array.isArray(tmpls) ? tmpls : []);
      } catch (_) {
        setTemplates([]);
      }
    };
    bootstrap();
  }, []);

  // Build a key -> [values] map from user's existing tags for value auto-suggest in Key: Value entries
  useEffect(() => {
    const next = {};
    const ensureSet = (k) => {
      const key = String(k || '').trim().toLowerCase();
      if (!key) return null;
      if (!next[key]) next[key] = new Set();
      return next[key];
    };
    for (const t of allTags) {
      const raw = (t && t.name) ? String(t.name) : '';
      const m = raw.match(/^\s*([^:=\-]+?)\s*[:=\-]\s*(.+)$/);
      if (m) {
        const k = m[1];
        const v = m[2];
        const set = ensureSet(k);
        if (set && v) set.add(String(v).trim());
      }
    }
    const obj = {};
    Object.keys(next).forEach(k => obj[k] = Array.from(next[k]));
    setKeyToValues(obj);
  }, [allTags]);

  const ext = useMemo(() => {
    const n = String(file?.filename || '');
    const parts = n.split('.');
    return parts.length > 1 ? `.${parts.pop()}` : '';
  }, [file]);

  const baseName = useMemo(() => {
    const n = String(file?.filename || '');
    return n.replace(/\.[^.]+$/, '');
  }, [file]);

  useEffect(() => {
    // Initialize with base name without extension
    setNameInput(baseName);
  }, [baseName]);

  // Tag parsing helpers (same behavior as UploadReviewModal)
  const sanitize = (s) => (s || '')
    .toString()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_\-]+/g, '')
    .replace(/^_+|_+$/g, '') || '';

  const tagKVs = useMemo(() => selectedTags.map(raw => {
    const m = String(raw).match(/^\s*([^:=\-]+?)\s*[:=\-]\s*(.+)$/);
    if (m) return { key: m[1].trim().toLowerCase(), value: m[2].trim(), raw };
    return { key: null, value: String(raw).trim(), raw };
  }), [selectedTags]);

  const tagValues = useMemo(() => tagKVs.map(kv => kv.value).filter(Boolean), [tagKVs]);

  const getValueForKey = useCallback((rx) => {
    const kv = tagKVs.find(k => k.key && rx.test(k.key));
    return kv ? kv.value : null;
  }, [tagKVs]);

  const looksLikeName = (n) => {
    if (!n) return false;
    if (/\s+/.test(n) && /^[A-Za-z][A-Za-z\-']+(\s+[A-Za-z\-']+)+$/.test(n)) return true;
    if (/^[A-Z][a-z]{2,}$/.test(n)) return true;
    return false;
  };

  const findAge = useCallback(() => {
    const candidates = [...tagValues];
    for (let raw of candidates) {
      const n = (raw || '').toString().trim().toLowerCase().replace(/[,]/g, '');
      if (!n) continue;
      let m = n.match(/^(\d{1,2})\s*(y|yr|yrs|year|years)$/i); if (m) return `${m[1]}y`;
      m = n.match(/^(\d{1,3})\s*(m|mo|mos|month|months)$/i); if (m) return `${m[1]}mo`;
      m = n.match(/^age[:=\s]+(\d{1,3})\s*(years?|yrs?)?$/i); if (m) return `${m[1]}y`;
      m = n.match(/^age[:=\s]+(\d{1,3})\s*(months?|mos?)$/i); if (m) return `${m[1]}mo`;
      m = n.match(/^(\d{1,2})y$/i); if (m) return `${m[1]}y`;
      m = n.match(/^(\d{1,3})mo$/i); if (m) return `${m[1]}mo`;
      m = n.match(/^(\d{1,2})\s*[- ]?month(s)?$/i); if (m) return `${m[1]}mo`;
    }
    return null;
  }, [tagValues]);

  const findPerson = useCallback(() => {
    const val = getValueForKey(/^(person|name|who)$/i);
    if (val) return val;
    const candidate = tagValues.find(looksLikeName);
    return candidate || null;
  }, [getValueForKey, tagValues]);

  const findEvent = useCallback(() => {
    const keyed = getValueForKey(/^(event|occasion)$/i);
    if (keyed) return keyed;
    const keywords = /(birthday|wedding|anniversary|graduation|vacation|party|hike|trip|holiday|christmas|new\s*year|halloween|easter|festival|concert)/i;
    for (const n of tagValues) { if (keywords.test(n)) return n; }
    return null;
  }, [getValueForKey, tagValues]);

  const buildProposedBaseName = useCallback(() => {
    const name = file?.filename || '';
    const extOnly = (name.split('.').pop() || '').toLowerCase();
    const imageExts = ['jpg','jpeg','png','gif','webp','bmp','svg'];
    const videoExts = ['mp4','mov','avi','mkv','wmv','flv','webm','m4v'];
    const person = findPerson();
    const age = findAge();
    const eventVal = findEvent();
    const second = age || eventVal || null;
    if (imageExts.includes(extOnly) || videoExts.includes(extOnly)) {
      const p = sanitize(person) || 'person';
      const s = sanitize(second) || (age ? '1y' : 'event');
      return `${p}_${s}`;
    }
    const isSpreadsheet = ['csv','xls','xlsx'].includes(extOnly);
    if (isSpreadsheet) {
      const proj = sanitize(getValueForKey(/^(project)$/i)) || 'project';
      const kind = 'data';
      const ver = 'v1';
      return `${proj}_${kind}_${ver}`;
    }
    return name.replace(/\.[^.]+$/, '') || 'file';
  }, [file, findPerson, findAge, findEvent, getValueForKey]);

  const suggestedBase = useMemo(() => buildProposedBaseName(), [buildProposedBaseName]);

  const updateValueSuggestions = useCallback((input) => {
    const str = String(input || '');
    const m = str.match(/^\s*([^:=\-]+?)\s*[:=\-]\s*(.*)$/);
    if (!m) {
      setValueSuggestions([]);
      setShowValueSuggestions(false);
      setActiveSuggestionIndex(-1);
      return;
    }
    const key = m[1].trim().toLowerCase();
    const prefix = (m[2] || '').toString().trim().toLowerCase();
    const candidates = keyToValues[key] || [];
    let filtered = candidates;
    if (prefix) filtered = candidates.filter(v => String(v).toLowerCase().includes(prefix));
    filtered = filtered.slice(0, 8);
    setValueSuggestions(filtered);
    setShowValueSuggestions(filtered.length > 0);
    setActiveSuggestionIndex(filtered.length ? 0 : -1);
  }, [keyToValues]);

  const toggleTag = (name) => {
    setSelectedTags((prev) => {
      return prev.includes(name) ? prev.filter((t) => t !== name) : [...prev, name];
    });
  };

  const addDraftTag = () => {
    const t = (newTagDraft || '').trim();
    if (!t) return;
    setSelectedTags((prev) => (prev.includes(t) ? prev : [...prev, t]));
    setNewTagDraft('');
    setShowValueSuggestions(false);
  };

  const ensureTagsExistAndGetIds = async (tagNames) => {
    if (!tagNames || tagNames.length === 0) return [];
    let existing = [];
    try { existing = await getUserTags(); } catch (_) { existing = []; }
    const nameToId = new Map((existing || []).map((t) => [String(t.name || '').trim().toLowerCase(), t.tag_id]));
    const ids = [];
    for (const name of tagNames) {
      const key = String(name || '').trim().toLowerCase();
      if (!key) continue;
      let id = nameToId.get(key);
      if (!id) {
        try {
          const res = await createTag({ name });
          id = res?.tag_id;
          if (id) nameToId.set(key, id);
        } catch (_) {}
      }
      if (id) ids.push(id);
    }
    return ids;
  };

  const handleSave = async () => {
    if (!file) return;
    setSaving(true);
    try {
      // 1) Rename if changed (preserve extension)
      const proposed = `${(nameInput || '').trim()}${ext}`;
      if (proposed && proposed !== file.filename) {
        await renameFile(file.upload_id, proposed);
      }

      // 2) Update tags (by names for AI context)
      await updateFileTags(file.upload_id, selectedTags);
      // Also ensure tag entities exist and are linked, so Tags view reflects usage
      try {
        const tagIds = await ensureTagsExistAndGetIds(selectedTags);
        for (const tagId of tagIds) {
          try { await addTagToFiles(tagId, [file.upload_id]); } catch (_) {}
        }
      } catch (_) {}

      // 3) Apply template if chosen (apply to this file only)
      if (selectedTemplateId) {
        try {
          await applySortingTemplate(selectedTemplateId, [file.upload_id]);
        } catch (_) {
          // Non-fatal; continue
        }
      }

      const updated = {
        ...file,
        filename: (proposed && proposed !== file.filename) ? proposed : file.filename,
        original_name: (proposed && proposed !== file.filename) ? proposed : file.filename,
        tags: selectedTags,
      };
      onSaved && onSaved(updated);
      onClose && onClose();
    } catch (e) {
      alert(e.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-3xl bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Quick Edit</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-100">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="p-5 space-y-5 max-h-[70vh] overflow-auto">
          {/* Rename */}
          <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Filename</label>
            <div className="flex items-center gap-2">
              <input
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                placeholder={baseName}
              />
              <span className="text-sm text-gray-500 dark:text-gray-400 select-none">{ext}</span>
            </div>
            <div className="mt-2 flex items-center justify-between text-xs">
              <div className="text-gray-500 dark:text-gray-400">
                Suggested: <span className="font-medium text-gray-700 dark:text-gray-200">{suggestedBase}{ext}</span>
              </div>
              <button
                type="button"
                onClick={() => setNameInput(suggestedBase)}
                className="px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200"
              >
                Use suggested
              </button>
            </div>
          </div>

          {/* Tags */}
          <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">Tags</label>
            {allTags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {allTags.map((t) => (
                  <button
                    key={t.tag_id}
                    type="button"
                    onClick={() => toggleTag(t.name)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                      selectedTags.includes(t.name)
                        ? 'text-white'
                        : 'bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'
                    }`}
                    style={{ backgroundColor: selectedTags.includes(t.name) ? (t.color || '#3B82F6') : undefined }}
                  >
                    <i className={`fas fa-${t.icon || 'tag'} mr-1`}></i>
                    {t.name}
                  </button>
                ))}
              </div>
            )}
            <div className="flex gap-2 relative">
              <input
                value={newTagDraft}
                onChange={(e) => { setNewTagDraft(e.target.value); updateValueSuggestions(e.target.value); }}
                onFocus={() => updateValueSuggestions(newTagDraft)}
                onKeyDown={(e) => {
                  if (!showValueSuggestions || valueSuggestions.length === 0) {
                    if (e.key === 'Enter') { e.preventDefault(); addDraftTag(); }
                    return;
                  }
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setActiveSuggestionIndex((idx) => (idx + 1) % valueSuggestions.length);
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setActiveSuggestionIndex((idx) => (idx - 1 + valueSuggestions.length) % valueSuggestions.length);
                  } else if (e.key === 'Enter') {
                    const str = String(newTagDraft || '');
                    const m = str.match(/^\s*([^:=\-]+?)\s*[:=\-]\s*(.*)$/);
                    if (m && activeSuggestionIndex >= 0) {
                      e.preventDefault();
                      const key = m[1].trim();
                      const chosen = valueSuggestions[activeSuggestionIndex];
                      setNewTagDraft(`${key}: ${chosen}`);
                      setShowValueSuggestions(false);
                    }
                  } else if (e.key === 'Escape') {
                    setShowValueSuggestions(false);
                  }
                }}
                className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                placeholder="Add new tag (Name or Key: Value)"
              />
              <button onClick={addDraftTag} className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded">Add</button>
              {showValueSuggestions && valueSuggestions.length > 0 && (
                <div className="absolute left-0 right-24 mt-9 max-h-48 overflow-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow-lg z-10">
                  {valueSuggestions.map((v, idx) => (
                    <div
                      key={v + idx}
                      className={`px-3 py-2 text-sm cursor-pointer ${idx === activeSuggestionIndex ? 'bg-blue-50 dark:bg-blue-900/30' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                      onMouseEnter={() => setActiveSuggestionIndex(idx)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        const str = String(newTagDraft || '');
                        const m = str.match(/^\s*([^:=\-]+?)\s*[:=\-]\s*(.*)$/);
                        const key = m ? m[1].trim() : '';
                        setNewTagDraft(`${key}: ${v}`);
                        setShowValueSuggestions(false);
                      }}
                    >
                      {v}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {selectedTags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {selectedTags.map((t) => (
                  <span key={t} className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">
                    {t}
                    <button className="ml-1" onClick={() => toggleTag(t)}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Template */}
          <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">Apply Template (optional)</label>
            <select
              value={selectedTemplateId || ''}
              onChange={(e) => setSelectedTemplateId(e.target.value ? Number(e.target.value) : null)}
              className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">None</option>
              {templates.map((t) => (
                <option key={t.template_id} value={t.template_id}>{t.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center gap-3">
          <button onClick={onClose} className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 flex-1">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving || !(nameInput || '').trim()}
            className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white flex-1"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuickEditModal;


